<template>
  <div class="question-bank-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>题库管理</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-plus" @click="showCreateBankDialog">创建题库</el-button>
      </div>
    </div>

    <!-- 题库列表 -->
    <div class="bank-list" v-loading="loading">
      <div v-if="questionBanks.length === 0" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <p>暂无题库，请创建您的第一个题库</p>
      </div>

      <el-row :gutter="20" v-else>
        <el-col :xs="24" :sm="12" :md="8" :lg="6" v-for="bank in questionBanks" :key="bank.id">
          <div class="bank-card" @click="openBankDetail(bank)">
            <div class="bank-header">
              <h3>{{ bank.name }}</h3>
              <div class="bank-actions">
                <el-button type="text" icon="el-icon-edit" @click.stop="editBank(bank)"></el-button>
                <el-button type="text" icon="el-icon-delete" @click.stop="deleteBank(bank)"></el-button>
              </div>
            </div>
            <div class="bank-content">
              <p class="bank-description">{{ bank.description || '暂无描述' }}</p>
              <div class="bank-meta">
                <span class="meta-item">
                  <i class="el-icon-folder"></i>
                  {{ bank.folderCount || 0 }} 个文件夹
                </span>
                <span class="meta-item">
                  <i class="el-icon-document"></i>
                  {{ bank.questionCount || 0 }} 道题目
                </span>
              </div>
            </div>
            <div class="bank-footer">
              <span class="bank-status" :class="{ 'public': bank.isPublic }">
                {{ bank.isPublic ? '公开' : '私有' }}
              </span>
              <span class="bank-time">{{ formatTime(bank.createdTime) }}</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 创建/编辑题库对话框 -->
    <el-dialog
      :title="isEdit ? '编辑题库' : '创建题库'"
      :visible.sync="bankDialogVisible"
      width="500px"
      @close="resetBankForm"
    >
      <el-form :model="bankForm" :rules="bankRules" ref="bankForm" label-width="80px">
        <el-form-item label="题库名称" prop="name">
          <el-input v-model="bankForm.name" placeholder="请输入题库名称"></el-input>
        </el-form-item>
        <el-form-item label="题库描述" prop="description">
          <el-input
            type="textarea"
            v-model="bankForm.description"
            placeholder="请输入题库描述"
            :rows="3"
          ></el-input>
        </el-form-item>
        <el-form-item label="关联课程" prop="courseId">
          <el-select v-model="bankForm.courseId" placeholder="请选择关联课程（可选）" clearable :disabled="!!courseId">
            <el-option
              v-for="course in courses"
              :key="course.id"
              :label="course.name"
              :value="course.id"
            ></el-option>
          </el-select>
          <div v-if="courseId" class="form-tip">当前页面仅显示当前课程的题库</div>
        </el-form-item>
        <el-form-item label="公开设置" prop="isPublic">
          <el-radio-group v-model="bankForm.isPublic">
            <el-radio :label="0">私有</el-radio>
            <el-radio :label="1">公开</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="bankDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBankForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 题库详情对话框 -->
    <el-dialog
      title="题库详情"
      :visible.sync="bankDetailVisible"
      width="80%"
      :before-close="closeBankDetail"
    >
      <div v-if="currentBank" class="bank-detail">
        <!-- 题库信息 -->
        <div class="bank-info">
          <h3>{{ currentBank.name }}</h3>
          <p>{{ currentBank.description || '暂无描述' }}</p>
          <div class="bank-stats">
            <span>文件夹: {{ currentBank.folderCount || 0 }}</span>
            <span>题目: {{ currentBank.questionCount || 0 }}</span>
            <span>状态: {{ currentBank.isPublic ? '公开' : '私有' }}</span>
          </div>
        </div>

        <!-- 文件夹管理 -->
        <div class="folder-management">
          <div class="folder-header">
            <h4>文件夹管理</h4>
            <el-button type="primary" size="small" icon="el-icon-plus" @click="showCreateFolderDialog">
              新建文件夹
            </el-button>
          </div>

          <!-- 文件夹树 -->
          <div class="folder-tree" v-loading="folderLoading">
            <el-tree
              :data="folderTree"
              :props="treeProps"
              node-key="id"
              :expand-on-click-node="false"
              :default-expand-all="true"
              @node-click="onFolderNodeClick"
            >
              <span class="custom-tree-node" slot-scope="{ node, data }">
                <span class="tree-node-label">
                  <i class="el-icon-folder"></i>
                  {{ node.label }}
                  <span class="question-count">({{ data.questionCount || 0 }})</span>
                </span>
                <span class="tree-node-actions">
                  <el-button type="text" size="mini" icon="el-icon-plus" @click="showCreateSubFolderDialog(data)">
                  </el-button>
                  <el-button type="text" size="mini" icon="el-icon-edit" @click="editFolder(data)">
                  </el-button>
                  <el-button type="text" size="mini" icon="el-icon-delete" @click="deleteFolder(data)">
                  </el-button>
                </span>
              </span>
            </el-tree>
          </div>
        </div>

        <!-- 题目管理 -->
        <div class="question-management">
          <div class="question-header">
            <h4>题目管理</h4>
            <div class="question-actions">
              <el-button
                type="success"
                size="small"
                icon="el-icon-magic-stick"
                @click="batchCompleteQuestionInfo"
                :disabled="!selectedQuestions || selectedQuestions.length === 0"
                style="margin-right: 10px;"
              >
                补全选中题目信息
              </el-button>
              <el-dropdown trigger="click" @command="handleImportCommand">
                <el-button type="primary" size="small" icon="el-icon-upload2">
                  导入题目 <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="manual">手动添加</el-dropdown-item>
                  <el-dropdown-item command="excel">Excel导入</el-dropdown-item>
                  <el-dropdown-item command="ocr">OCR导入</el-dropdown-item>
                  <el-dropdown-item command="pdf">PDF导入</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>

          <!-- 题目列表 -->
          <div class="question-list" v-loading="questionLoading" style="overflow-x: auto;">
            <el-table :data="questions" border fit highlight-current-row :header-cell-style="{
              background: '#f2f3f4',
              color: '#555',
              'font-weight': 'bold',
              'line-height': '32px',
            }" style="min-width: 2000px;" @selection-change="handleSelectionChange">
              <el-table-column align="center" type="selection" width="55" fixed="left" />
              <el-table-column label="序号" align="center" width="80" fixed="left">
                <template slot-scope="scope">{{ scope.$index + 1 }}</template>
              </el-table-column>
              <el-table-column prop="id" label="ID" align="center" width="80" />
              <el-table-column prop="content" label="题干" align="center" min-width="300" show-overflow-tooltip />
              <el-table-column prop="image" label="图片" align="center" width="120">
                <template slot-scope="scope">
                  <div v-if="scope.row.image" class="image-cell">
                    <img
                      :src="getImageUrl(scope.row.image)"
                      :alt="scope.row.image"
                      class="thumbnail-image"
                      @click="previewImage(scope.row.image)"
                      @error="handleImageError"
                    />
                    <div class="image-name" :title="scope.row.image">{{ getImageName(scope.row.image) }}</div>
                  </div>
                  <span v-else style="color: #999;">无</span>
                </template>
              </el-table-column>
              <el-table-column prop="repoTitle" label="所属题库" align="center" width="120" />
              <el-table-column label="题目类型" align="center" width="100">
                <template slot-scope="scope">
                  <span v-if="scope.row.quType == 1">选择题</span>
                  <span v-else-if="scope.row.quType == 2">填空题</span>
                  <span v-else-if="scope.row.quType == 3">判断题</span>
                  <span v-else-if="scope.row.quType == 4">简答题</span>
                </template>
              </el-table-column>
              <el-table-column prop="optionsStr" label="选项" align="center" min-width="200" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span v-if="scope.row.optionsStr">{{ scope.row.optionsStr }}</span>
                  <span v-else style="color: #999;">无</span>
                </template>
              </el-table-column>
              <el-table-column prop="answer" label="答案" align="center" width="100" show-overflow-tooltip />
              <el-table-column prop="analysis" label="解析" align="center" min-width="200" show-overflow-tooltip />
              <el-table-column prop="createTime" label="创建时间" align="center" width="160" />
              <el-table-column prop="knowledge" label="知识点" align="center" width="120" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span v-if="scope.row.knowledge">{{ scope.row.knowledge }}</span>
                  <span v-else style="color: #999;">无</span>
                </template>
              </el-table-column>
              <el-table-column prop="point" label="分数" align="center" width="80" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span v-if="scope.row.point">{{ scope.row.point }}</span>
                  <span v-else style="color: #999;">无</span>
                </template>
              </el-table-column>
              <el-table-column prop="realName" label="录题人" align="center" width="100" />
              <el-table-column prop="type" label="类型" align="center" width="100" />
              <el-table-column prop="level" label="难度等级" align="center" width="100">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.level == 1" type="success">简单</el-tag>
                  <el-tag v-else-if="scope.row.level == 2" type="warning">中等</el-tag>
                  <el-tag v-else-if="scope.row.level == 3" type="danger">难</el-tag>
                  <el-tag v-else-if="scope.row.level == 4" type="info">非常难</el-tag>
                  <span v-else>{{ scope.row.level }}</span>
                </template>
              </el-table-column>
                <el-table-column prop="url" label="链接" align="center" width="120" show-overflow-tooltip>
                  <template slot-scope="scope">
                    <a
                      v-if="scope.row.url"
                      :href="formatUrl(scope.row.url)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="url-link"
                      :title="scope.row.url"
                    >
                      {{ getUrlDisplayText(scope.row.url) }}
                    </a>
                    <span v-else style="color: #999;">无</span>
                  </template>
                </el-table-column>
              <el-table-column prop="difficulty" label="难度系数" align="center" width="100">
                <template slot-scope="scope">
                  <span v-if="scope.row.difficulty">{{ scope.row.difficulty }}</span>
                  <span v-else style="color: #999;">无</span>
                </template>
              </el-table-column>
              <el-table-column align="center" label="操作" width="200" fixed="right">
                <template slot-scope="{ row }">
                  <el-button type="text" size="small" style="font-size: 14px" @click="editQuestion(row)">编辑</el-button>
                  <el-button type="text" size="small" style="font-size: 14px" @click="moveQuestion(row)">移动</el-button>
                  <el-button type="text" size="small" style="color: red; font-size: 14px" @click="removeQuestion(row)">移除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 编辑题目对话框 -->
    <el-dialog
      title="编辑题目"
      :visible.sync="editQuestionDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      :modal-append-to-body="true"
      append-to-body
      class="edit-question-dialog"
    >
      <el-form ref="editQuestionForm" :model="editQuestionForm" :rules="editQuestionRules" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="题目类型" prop="quType">
              <el-select v-model="editQuestionForm.quType" placeholder="请选择题型" style="width: 100%">
                <el-option label="选择题" :value="1"></el-option>
                <el-option label="填空题" :value="2"></el-option>
                <el-option label="判断题" :value="3"></el-option>
                <el-option label="简答题" :value="4"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分数" prop="point">
              <el-input-number v-model="editQuestionForm.point" :min="0" :max="100" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="题目内容" prop="content">
          <el-input v-model="editQuestionForm.content" type="textarea" :rows="4" placeholder="请输入题目内容"></el-input>
        </el-form-item>

        <el-form-item label="试题图片">
          <!-- 有图片时显示图片和操作按钮 -->
          <div v-if="editQuestionForm.image" style="border: 1px solid #e4e7ed; border-radius: 4px; padding: 15px; background-color: #fafafa;">
            <div style="display: flex; align-items: flex-start; gap: 15px;">
              <img
                :src="getImageUrl(editQuestionForm.image)"
                :alt="getImageName(editQuestionForm.image)"
                style="width: 120px; height: 80px; object-fit: cover; border-radius: 4px; border: 1px solid #e4e7ed; cursor: pointer; flex-shrink: 0;"
                @click="previewImage(editQuestionForm.image)"
                @error="handleImageError"
              />
              <div style="flex: 1; min-width: 0;">
                <div style="color: #333; font-size: 14px; margin-bottom: 8px; font-weight: 500;">{{ getImageName(editQuestionForm.image) }}</div>
                <div style="display: flex; gap: 10px; margin-bottom: 10px;">
                  <el-button type="primary" size="mini" @click="triggerImageUpload">
                    <i class="el-icon-edit"></i> 更换图片
                  </el-button>
                  <el-button type="danger" size="mini" @click="removeEditImage">
                    <i class="el-icon-delete"></i> 移除图片
                  </el-button>
                </div>
                <div style="color: #999; font-size: 12px;">点击图片可预览大图</div>
              </div>
            </div>
          </div>

          <!-- 没有图片时显示上传区域 -->
          <div v-else>
            <el-upload
              class="upload-demo"
              drag
              action=""
              :auto-upload="false"
              :on-change="handleEditImageChange"
              :on-remove="handleEditImageRemove"
              :file-list="editImageFileList"
              accept=".jpg,.jpeg,.png"
              :limit="1"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将图片拖到此处，或<em>点击上传</em></div>
              <div slot="tip" class="el-upload__tip">只能上传jpg/png文件，且不超过2MB</div>
            </el-upload>
          </div>

          <!-- 隐藏的文件上传input，用于更换图片 -->
          <input
            ref="hiddenFileInput"
            type="file"
            accept=".jpg,.jpeg,.png"
            style="display: none;"
            @change="handleHiddenFileChange"
          />
        </el-form-item>

        <el-form-item label="选项" v-if="editQuestionForm.quType === 1 || editQuestionForm.quType === 2">
          <el-input v-model="editQuestionForm.optionsStr" type="textarea" :rows="3" placeholder="请输入选项，多个选项用分号分隔"></el-input>
        </el-form-item>

        <el-form-item label="答案" prop="answer">
          <el-input v-model="editQuestionForm.answer" type="textarea" :rows="2" placeholder="请输入答案"></el-input>
        </el-form-item>

        <el-form-item label="解析">
          <el-input v-model="editQuestionForm.analysis" type="textarea" :rows="3" placeholder="请输入题目解析"></el-input>
        </el-form-item>

        <el-form-item label="知识点">
          <!-- 使用原生select，支持动态数据 -->
          <select
            v-model="editQuestionForm.knowledge"
            style="width: 100%; height: 40px; border: 1px solid #dcdfe6; border-radius: 4px; padding: 0 15px; background-color: #fff;"
            @focus="handleSelectFocus"
          >
            <option value="">请选择知识点</option>
            <option
              v-for="option in flattenedKnowledgeOptions"
              :key="option.uniqueKey"
              :value="option.name"
            >
              {{ option.displayName }}
            </option>
          </select>
          <div class="form-tip">请选择知识点</div>
          <div v-if="knowledgeOptions.length === 0" style="color: #f56c6c; font-size: 12px; margin-top: 4px;">
            暂无知识点数据，请在"课程知识点"中添加知识点！
          </div>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="难度等级">
              <el-select
                v-model="editQuestionForm.level"
                placeholder="请选择难度"
                style="width: 100%"
                :popper-append-to-body="false"
                popper-class="edit-question-select-dropdown"
                @change="handleLevelChange"
              >
                <el-option label="简单" :value="1"></el-option>
                <el-option label="中等" :value="2"></el-option>
                <el-option label="难" :value="3"></el-option>
                <el-option label="非常难" :value="4"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="难度系数">
              <el-input-number v-model="editQuestionForm.difficulty" :min="0" :max="1" :step="0.1" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="链接">
          <el-input
            v-model="editQuestionForm.link"
            placeholder="请输入相关链接（可选）"
            clearable
          >
            <template slot="prepend">http://</template>
          </el-input>
          <div class="form-tip">可以添加与题目相关的参考链接、视频链接等</div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
          <el-button type="info" @click="autoCompleteQuestionInfo" :loading="autoCompleteLoading">
            <i class="el-icon-magic-stick"></i> 自动补全题目信息
          </el-button>
          <div>
            <el-button @click="editQuestionDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="saveEditQuestion" :loading="editQuestionLoading">保存</el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 创建/编辑文件夹对话框 -->
    <el-dialog
      :title="isEditFolder ? '编辑文件夹' : '新建文件夹'"
      :visible.sync="folderDialogVisible"
      width="400px"
      @close="resetFolderForm"
    >
      <el-form :model="folderForm" :rules="folderRules" ref="folderForm" label-width="80px">
        <el-form-item label="文件夹名" prop="name">
          <el-input v-model="folderForm.name" placeholder="请输入文件夹名称"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            type="textarea"
            v-model="folderForm.description"
            placeholder="请输入文件夹描述"
            :rows="2"
          ></el-input>
        </el-form-item>
        <el-form-item label="父文件夹" prop="parentId" v-if="!isEditFolder">
          <el-select v-model="folderForm.parentId" placeholder="请选择父文件夹">
            <el-option label="根目录" :value="0"></el-option>
            <el-option
              v-for="folder in allFolders"
              :key="folder.id"
              :label="folder.name"
              :value="folder.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="folderDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitFolderForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- Excel导入对话框 -->
    <el-dialog
      title="Excel导入"
      :visible.sync="excelImportDialogVisible"
      width="500px"
    >
      <el-form label-width="100px">
        <el-form-item label="目标文件夹">
          <el-select v-model="importFolderId" placeholder="请选择目标文件夹" style="width: 100%">
            <el-option label="根目录" :value="0"></el-option>
            <el-option
              v-for="folder in allFolders"
              :key="folder.id"
              :label="folder.name"
              :value="folder.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="选择文件">
          <el-upload
            class="upload-demo"
            drag
            action=""
            :auto-upload="false"
            :on-change="handleExcelFileChange"
            :on-remove="handleExcelFileRemove"
            :file-list="excelFileList"
            accept=".xls,.xlsx"
            :limit="1"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将Excel文件拖到此处，或<em>点击上传</em></div>
            <div slot="tip" class="el-upload__tip">只能上传xls/xlsx文件</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="excelImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExcelFile" :loading="uploading">提交</el-button>
      </div>
    </el-dialog>

    <!-- OCR导入对话框 -->
    <el-dialog
      title="OCR识别题目"
      :visible.sync="ocrImportDialogVisible"
      width="600px"
    >
      <el-form label-width="100px">
        <el-form-item label="目标文件夹">
          <el-select v-model="importFolderId" placeholder="请选择目标文件夹" style="width: 100%">
            <el-option label="根目录" :value="0"></el-option>
            <el-option
              v-for="folder in allFolders"
              :key="folder.id"
              :label="folder.name"
              :value="folder.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 上传题目图片 -->
        <el-form-item label="上传题目">
          <el-upload
            class="upload-demo"
            drag
            action=""
            multiple
            :limit="10"
            accept=".jpg,.jpeg,.png"
            :auto-upload="false"
            :on-remove="handleOcrFileRemove"
            :on-change="handleOcrFileChange"
            :file-list="ocrFileList"
          >
            <i class="el-icon-upload" />
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div slot="tip" class="el-upload__tip">支持多张图片，建议jpg/png</div>
          </el-upload>
        </el-form-item>

        <!-- 上传答案图片 -->
        <el-form-item label="上传答案">
          <el-upload
            class="upload-demo"
            drag
            action=""
            multiple
            :limit="10"
            accept=".jpg,.jpeg,.png"
            :auto-upload="false"
            :on-remove="handleAnswersFileRemove"
            :on-change="handleAnswersFileChange"
            :file-list="answersFileList"
          >
            <i class="el-icon-upload" />
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div slot="tip" class="el-upload__tip">支持多张图片，建议jpg/png</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="ocrImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitOcrFiles" :loading="uploading">确定</el-button>
      </div>
    </el-dialog>

    <!-- PDF导入对话框 -->
    <el-dialog
      title="PDF导入"
      :visible.sync="pdfImportDialogVisible"
      width="500px"
    >
      <el-form label-width="100px">
        <el-form-item label="目标文件夹">
          <el-select v-model="importFolderId" placeholder="请选择目标文件夹" style="width: 100%">
            <el-option label="根目录" :value="0"></el-option>
            <el-option
              v-for="folder in allFolders"
              :key="folder.id"
              :label="folder.name"
              :value="folder.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="选择文件">
          <el-upload
            class="upload-demo"
            drag
            action=""
            :auto-upload="false"
            :file-list="pdfFileList"
            :on-change="handlePdfFileChange"
            :on-remove="handlePdfFileRemove"
            accept=".pdf"
            :limit="1"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将PDF文件拖到此处，或<em>点击上传</em></div>
            <div slot="tip" class="el-upload__tip">只能上传PDF文件</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="pdfImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPdfFile" :loading="uploading">提交</el-button>
      </div>
    </el-dialog>

    <!-- 手动添加题目对话框 -->
    <el-dialog
      title="手动添加题目"
      :visible.sync="manualAddDialogVisible"
      width="700px"
    >
      <el-form :model="manualQuestionForm" ref="manualQuestionForm" label-width="100px">
        <el-form-item label="题目内容" prop="content" required>
          <el-input
            type="textarea"
            v-model="manualQuestionForm.content"
            placeholder="请输入题目内容"
            :rows="4"
          ></el-input>
        </el-form-item>
        <el-form-item label="题目类型" prop="quType" required>
          <el-radio-group v-model="manualQuestionForm.quType">
            <el-radio :label="1">选择题</el-radio>
            <el-radio :label="2">判断题</el-radio>
            <el-radio :label="3">填空题</el-radio>
            <el-radio :label="4">简答题</el-radio>
          </el-radio-group>
        </el-form-item>
          <el-form-item label="选项" v-if="manualQuestionForm.quType === 1" required>
            <el-input
              v-model="manualQuestionForm.options"
              placeholder="请输入选项，用分号分隔，例如：A.选项1;B.选项2;C.选项3;D.选项4"
              type="textarea"
              :rows="3"
            ></el-input>
            <div class="form-tip">选择题至少需要两个选项，用分号(;)分隔</div>
          </el-form-item>
        <el-form-item label="答案" prop="answer">
          <el-input v-model="manualQuestionForm.answer" placeholder="请输入答案"></el-input>
        </el-form-item>
        <el-form-item label="解析">
          <el-input
            type="textarea"
            v-model="manualQuestionForm.analysis"
            placeholder="请输入解析"
            :rows="3"
          ></el-input>
        </el-form-item>
        <el-form-item label="知识点">
          <el-cascader
            v-model="manualQuestionForm.knowledgePath"
            :options="knowledgeOptions"
            :props="knowledgeCascaderProps"
            placeholder="请选择知识点"
            clearable
            filterable
            style="width: 100%"
            @change="handleManualKnowledgeChange"
          ></el-cascader>
          <div class="form-tip">支持搜索，可选择多级知识点</div>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="难度等级">
              <el-select v-model="manualQuestionForm.level" placeholder="请选择难度" style="width: 100%">
                <el-option label="简单" :value="1"></el-option>
                <el-option label="中等" :value="2"></el-option>
                <el-option label="难" :value="3"></el-option>
                <el-option label="非常难" :value="4"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分数">
              <el-input-number v-model="manualQuestionForm.point" :min="1" :max="100" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="目标文件夹">
          <el-select v-model="importFolderId" placeholder="请选择目标文件夹" style="width: 100%">
            <el-option label="根目录" :value="0"></el-option>
            <el-option
              v-for="folder in allFolders"
              :key="folder.id"
              :label="folder.name"
              :value="folder.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="manualAddDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitManualQuestion" :loading="uploading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMyQuestionBanks,
  getQuestionBanksByCourseId,
  createQuestionBank,
  updateQuestionBank,
  deleteQuestionBank,
  getQuestionBankFolderTree,
  createQuestionBankFolder,
  updateQuestionBankFolder,
  deleteQuestionBankFolder,
  getFolderQuestions,
  addQuestionToFolder,
  removeQuestionFromFolder,
  getBankQuestions
} from '@/api/questionBank'
import { getTeachingCourses } from '@/api/course'
import { importQue, quAdd, saveOcrQuestions, quDetailbyContent, quUpdate, quDetail , processOcrImage } from '@/api/question'

import { getKnowledgeByCourse } from '@/api/knowledge'
import axios from 'axios'

export default {
  name: 'QuestionBankManagement',
  props: {
    courseId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      questionBanks: [],
      courses: [],

      // 级联选择器刷新key
      cascaderKey: 0,

      // 题库对话框
      bankDialogVisible: false,
      isEdit: false,
      bankForm: {
        name: '',
        description: '',
        courseId: null,
        isPublic: 0
      },
      bankRules: {
        name: [
          { required: true, message: '请输入题库名称', trigger: 'blur' }
        ]
      },

      // 题库详情
      bankDetailVisible: false,
      currentBank: null,
      folderLoading: false,
      questionLoading: false,
      folderTree: [],
      questions: [],
      selectedQuestions: [], // 选中的题目
      selectedFolderId: null,
      treeProps: {
        children: 'children',
        label: 'name'
      },

      // 文件夹对话框
      folderDialogVisible: false,
      isEditFolder: false,
      folderForm: {
        name: '',
        description: '',
        parentId: 0
      },
      folderRules: {
        name: [
          { required: true, message: '请输入文件夹名称', trigger: 'blur' }
        ]
      },
      allFolders: [],

      // 导入相关
      importing: false,
      uploading: false,
      importFolderId: 0,

      // Excel导入
      excelImportDialogVisible: false,
      excelFileList: [],

      // OCR导入
      ocrImportDialogVisible: false,
      ocrFileList: [],
      answersFileList: [], // 答案文件列表

      // PDF导入
      pdfImportDialogVisible: false,
      pdfFileList: [],

      // 手动添加
      manualAddDialogVisible: false,
      manualQuestionForm: {
        content: '',
        quType: 1,
        options: '',
        answer: '',
        analysis: '',
        knowledge: '',
        knowledgePath: [], // 知识点路径数组
        level: 2, // 默认中等难度
        point: 10 // 默认分数
      },

      // 知识点相关
      knowledgeOptions: [], // 知识点选项数据
      knowledgeCascaderProps: {
        value: 'id',
        label: 'name',
        children: 'children',
        checkStrictly: true, // 允许选择任意级别的节点
        emitPath: true // 返回完整路径，便于处理
      },

      // 编辑题目
      editQuestionDialogVisible: false,
      editQuestionLoading: false,
      autoCompleteLoading: false,
      editImageFileList: [],
      editQuestionForm: {
        id: null,
        content: '',
        quType: 1,
        optionsStr: '',
        answer: '',
        analysis: '',
        knowledge: '',
        knowledgePath: [], // 知识点路径数组
        point: 10,
        level: 1,
        difficulty: 0.5,
        image: '',
        link: '' // 链接字段
      },
      editQuestionRules: {
        content: [
          { required: true, message: '请输入题目内容', trigger: 'blur' }
        ],
        answer: [
          { required: true, message: '请输入答案', trigger: 'blur' }
        ],
        quType: [
          { required: true, message: '请选择题型', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    this.loadQuestionBanks()
    this.loadCourses()
  },
  computed: {
    // 扁平化知识点选项，用于select显示
    flattenedKnowledgeOptions() {
      const flatten = (options, parentPath = '') => {
        let result = []
        options.forEach(option => {
          // 创建唯一的key，避免重复
          const uniqueKey = parentPath ? `${parentPath}-${option.id}` : option.id.toString()
          const displayName = parentPath ? `${parentPath} > ${option.name}` : option.name

          result.push({
            id: option.id,
            name: option.name,
            uniqueKey: uniqueKey,
            displayName: displayName
          })

          if (option.children && option.children.length > 0) {
            result = result.concat(flatten(option.children, option.name))
          }
        })
        return result
      }

      return flatten(this.knowledgeOptions)
    }
  },
  methods: {
    // 加载课程知识点数据
    async loadCourseKnowledge() {
      console.log('开始加载课程知识点数据...')
      console.log('当前题库信息:', this.currentBank)

      if (!this.currentBank) {
        console.warn('当前题库为空，无法加载知识点')
        this.$message.warning('请先选择一个题库')
        return
      }

      if (!this.currentBank.courseId) {
        console.warn('当前题库没有关联课程，尝试使用第一个可用课程')
        console.log('题库信息:', {
          id: this.currentBank.id,
          name: this.currentBank.name,
          courseId: this.currentBank.courseId
        })

        // 如果没有关联课程，尝试使用第一个可用课程
        if (this.courses && this.courses.length > 0) {
          const defaultCourse = this.courses[0]
          console.log('使用默认课程:', defaultCourse)
          this.$message.info(`当前题库未关联课程，使用默认课程"${defaultCourse.name}"的知识点`)
          // 临时设置courseId
          this.currentBank.courseId = defaultCourse.id
        } else {
          this.$message.warning('当前题库未关联课程，且没有可用课程，无法加载知识点')
          return
        }
      }

      try {
        console.log('加载课程知识点，课程ID:', this.currentBank.courseId)
        console.log('API请求URL:', `/api/knowledge-points/byCourse/${this.currentBank.courseId}`)

        // 使用正确的API函数
        const response = await getKnowledgeByCourse(this.currentBank.courseId)
        console.log('知识点API完整响应:', response)
        console.log('响应状态码:', response.status)
        console.log('响应数据:', response.data)
        console.log('响应数据类型:', typeof response.data)

        if (response && response.data) {
          console.log('知识点原始数据:', response.data)
          console.log('知识点数据类型:', typeof response.data, '是否为数组:', Array.isArray(response.data))
          console.log('知识点数据长度:', response.data.length)

          // 将知识点数据转换为级联选择器需要的格式
          const transformedOptions = this.transformKnowledgeToCascader(response.data)
          console.log('转换后的知识点选项:', transformedOptions)
          console.log('转换后选项数量:', transformedOptions.length)

          // 详细检查数据结构
          if (transformedOptions.length > 0) {
            console.log('第一个选项详情:', JSON.stringify(transformedOptions[0], null, 2))
            console.log('选项是否包含id和name:', transformedOptions[0].hasOwnProperty('id'), transformedOptions[0].hasOwnProperty('name'))
          }

          // 使用Vue.set确保响应式更新
          this.$set(this, 'knowledgeOptions', transformedOptions)

          // 强制刷新级联选择器
          this.cascaderKey++
          console.log('级联选择器key已更新:', this.cascaderKey)

          // 延迟再次强制更新
          this.$nextTick(() => {
            this.cascaderKey++
            console.log('延迟更新级联选择器key:', this.cascaderKey)
          })

          if (this.knowledgeOptions.length === 0) {
            console.log('没有知识点数据，使用测试数据')
            // 使用测试数据确保功能可用
            this.knowledgeOptions = [
              {
                id: 1,
                name: '数学基础',
                children: [
                  { id: 11, name: '数与运算', children: [] },
                  { id: 12, name: '几何图形', children: [] }
                ]
              },
              {
                id: 2,
                name: '代数',
                children: [
                  { id: 21, name: '一元一次方程', children: [] },
                  { id: 22, name: '二元一次方程组', children: [] }
                ]
              },
              {
                id: 3,
                name: '几何',
                children: []
              }
            ]
            this.$message.warning('请在"课程知识点"中添加知识点！当前使用测试数据。')
          } else {
            console.log(`成功加载 ${this.knowledgeOptions.length} 个知识点分类`)
            this.$message.success(`已加载 ${this.knowledgeOptions.length} 个知识点分类`)
          }
        } else {
          this.knowledgeOptions = []
          console.warn('未获取到知识点数据:', response)
          this.$message.warning('请在"课程知识点"中添加知识点！')
        }
      } catch (error) {
        console.error('加载课程知识点失败:', error)
        console.error('错误详情:', {
          message: error.message,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data
        })

        // 使用测试数据确保功能可用
        this.knowledgeOptions = [
          {
            id: 1,
            name: '数学基础',
            children: [
              { id: 11, name: '数与运算', children: [] },
              { id: 12, name: '几何图形', children: [] }
            ]
          },
          {
            id: 2,
            name: '代数',
            children: [
              { id: 21, name: '一元一次方程', children: [] },
              { id: 22, name: '二元一次方程组', children: [] }
            ]
          },
          {
            id: 3,
            name: '几何',
            children: []
          }
        ]
        this.$message.warning('请在"课程知识点"中添加知识点！当前使用测试数据。')
      }
    },

    // 将知识点数据转换为级联选择器格式
    transformKnowledgeToCascader(knowledgeList) {
      console.log('开始转换知识点数据:', knowledgeList)

      if (!knowledgeList || !Array.isArray(knowledgeList)) {
        console.warn('知识点数据无效:', knowledgeList)
        return []
      }

      // 递归转换知识点数据，因为后端已经返回了包含children的树形结构
      const transformNode = (node) => {
        console.log('转换节点:', node)
        const transformed = {
          id: node.id,
          name: node.name || `知识点${node.id}`,
          children: node.children && node.children.length > 0
            ? node.children.map(child => transformNode(child))
            : []
        }
        console.log('转换后节点:', transformed)
        return transformed
      }

      // 转换所有根节点
      const result = knowledgeList.map(node => transformNode(node))
      console.log('最终转换结果:', result)
      return result
    },

    // 处理知识点选择变化
    handleKnowledgeChange(value) {
      console.log('知识点选择变化:', value)
      if (value && value.length > 0) {
        // 获取路径中的最后一个知识点ID
        const selectedId = value[value.length - 1]
        // 根据选中的知识点ID查找知识点名称
        const selectedKnowledge = this.getKnowledgeNameById(selectedId)
        this.editQuestionForm.knowledge = selectedKnowledge
        console.log('设置知识点为:', selectedKnowledge)
      } else {
        this.editQuestionForm.knowledge = ''
      }
    },

    // 处理级联选择器显示状态变化
    handleCascaderVisibleChange(visible) {
      console.log('级联选择器显示状态变化:', visible)
      console.log('当前知识点选项:', this.knowledgeOptions)
      console.log('选项数量:', this.knowledgeOptions.length)
      if (visible && this.knowledgeOptions.length > 0) {
        console.log('级联选择器应该显示选项')
        // 检查DOM元素是否存在
        this.$nextTick(() => {
          const cascaderMenus = document.querySelectorAll('.edit-question-cascader-dropdown .el-cascader-menu')
          console.log('找到的级联选择器菜单数量:', cascaderMenus.length)
          if (cascaderMenus.length > 0) {
            console.log('级联选择器菜单已渲染')
            const menuItems = document.querySelectorAll('.edit-question-cascader-dropdown .el-cascader-menu__item')
            console.log('菜单项数量:', menuItems.length)

            // 详细检查数据结构
            console.log('详细检查知识点选项数据结构:')
            this.knowledgeOptions.forEach((option, index) => {
              console.log(`选项${index}:`, {
                id: option.id,
                name: option.name,
                children: option.children,
                hasChildren: option.children && option.children.length > 0
              })
            })

            // 检查级联选择器配置
            console.log('级联选择器配置:', this.knowledgeCascaderProps)
          } else {
            console.warn('级联选择器菜单未找到，可能被CSS隐藏或未正确渲染')
          }
        })
      }
    },

    // 处理select焦点事件
    handleSelectFocus() {
      // 可以在这里添加必要的逻辑
    },

    // 自动补全题目信息
    autoCompleteQuestionInfo() {
      this.$message.info('自动补全题目信息功能待实现')
      // TODO: 实现自动补全功能
      // 1. 根据题目内容分析题目类型
      // 2. 自动生成选项（如果是选择题）
      // 3. 智能推荐知识点
      // 4. 自动设置难度等级
      // 5. 生成题目解析
    },

    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedQuestions = selection
      console.log('选中的题目数量:', selection.length)
    },

    // 批量补全选中题目信息
    batchCompleteQuestionInfo() {
      if (!this.selectedQuestions || this.selectedQuestions.length === 0) {
        this.$message.warning('请先选择要补全的题目')
        return
      }

      this.$message.info(`批量补全 ${this.selectedQuestions.length} 个题目信息功能待实现`)
      // TODO: 实现批量补全功能
      // 1. 遍历选中的题目
      // 2. 分析每个题目的内容
      // 3. 自动补全题目类型、选项、知识点、难度等级、解析等
      // 4. 批量保存更新
      console.log('选中的题目:', this.selectedQuestions)
    },

    // 触发隐藏的文件上传
    triggerImageUpload() {
      this.$refs.hiddenFileInput.click()
    },

    // 处理隐藏文件上传的变化
    async handleHiddenFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        // 验证文件类型
        if (!file.type.startsWith('image/')) {
          this.$message.error('只能上传图片文件')
          return
        }

        // 验证文件大小
        if (file.size > 2 * 1024 * 1024) {
          this.$message.error('图片大小不能超过2MB')
          return
        }

        try {
          // 上传图片到服务器
          const formData = new FormData()
          formData.append('file', file)

          // 导入uploadFile函数
          const { uploadFile } = await import('@/api/homework')
          const response = await uploadFile(formData)

          if (response.code === 1) {
            // 上传成功，设置图片路径
            let imagePath = response.data || response.msg || file.name

            // 如果返回的是"上传成功"这样的消息，使用原始文件名
            if (imagePath === '上传成功' || imagePath === 'success' || !imagePath.includes('.')) {
              imagePath = file.name
            }

            // 确保图片路径是有效的文件名
            if (!imagePath || imagePath === 'null' || imagePath === 'undefined') {
              imagePath = file.name
            }

            this.editQuestionForm.image = imagePath
            this.$message.success('图片更换成功')
            console.log('图片更换成功:', this.editQuestionForm.image)
          } else {
            this.$message.error('图片上传失败: ' + (response.msg || '未知错误'))
          }
        } catch (error) {
          console.error('图片上传失败:', error)
          this.$message.error('图片上传失败，请稍后重试')
        }
      }

      // 清空input值，允许重复选择同一文件
      event.target.value = ''
    },

    // 处理难度等级选择变化
    handleLevelChange(value) {
      console.log('难度等级选择变化:', value, '类型:', typeof value)
      this.editQuestionForm.level = Number(value)
      console.log('设置难度等级为:', this.editQuestionForm.level)
    },

    // 处理手动添加题目的知识点选择变化
    handleManualKnowledgeChange(value) {
      console.log('手动添加题目知识点选择变化:', value)
      if (value && value.length > 0) {
        // 获取选中的知识点名称
        const selectedKnowledge = this.getKnowledgeNameByPath(value)
        this.manualQuestionForm.knowledge = selectedKnowledge
        console.log('设置手动添加题目知识点为:', selectedKnowledge)
      } else {
        this.manualQuestionForm.knowledge = ''
      }
    },

    // 根据知识点ID获取知识点名称
    getKnowledgeNameById(knowledgeId) {
      if (!knowledgeId) return ''

      // 递归查找知识点
      const findKnowledge = (nodes) => {
        for (const node of nodes) {
          if (node.id === knowledgeId) {
            return node.name
          }
          if (node.children && node.children.length > 0) {
            const found = findKnowledge(node.children)
            if (found) return found
          }
        }
        return null
      }

      return findKnowledge(this.knowledgeOptions) || ''
    },

    // 根据路径获取知识点名称（保留用于兼容性）
    getKnowledgeNameByPath(path) {
      if (!path || path.length === 0) return ''

      let current = this.knowledgeOptions
      let name = ''

      for (let i = 0; i < path.length; i++) {
        const id = path[i]
        const node = current.find(item => item.id === id)
        if (node) {
          name = node.name
          current = node.children || []
        } else {
          break
        }
      }

      return name
    },

    // 根据知识点名称设置知识点路径
    setKnowledgePathFromName(knowledgeName) {
      if (!knowledgeName || !this.knowledgeOptions.length) {
        return
      }

      // 递归查找知识点路径
      const findKnowledgePath = (nodes, targetName, currentPath = []) => {
        for (const node of nodes) {
          const newPath = [...currentPath, node.id]
          if (node.name === targetName) {
            return newPath
          }
          if (node.children && node.children.length > 0) {
            const childPath = findKnowledgePath(node.children, targetName, newPath)
            if (childPath) {
              return childPath
            }
          }
        }
        return null
      }

      const knowledgePath = findKnowledgePath(this.knowledgeOptions, knowledgeName)
      if (knowledgePath) {
        this.editQuestionForm.knowledgePath = knowledgePath
        console.log('设置知识点路径:', knowledgePath, '对应知识点:', knowledgeName)
      } else {
        console.warn('未找到知识点路径:', knowledgeName)
      }
    },

    // 获取图片完整URL
    getImageUrl(imageName) {
      if (!imageName) return ''

      console.log('处理图片URL:', imageName)

      // 检查是否是无效的图片路径
      const invalidPaths = ['上传成功', 'success', '上传失败', 'failed', 'null', 'undefined']
      if (invalidPaths.includes(imageName) || !imageName.includes('.')) {
        console.log('检测到无效图片路径:', imageName)
        return '' // 返回空字符串，让图片不显示
      }

      // 如果是完整的URL，直接返回
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        console.log('完整URL，直接返回:', imageName)
        return imageName
      }

      // 检查是否是本地测试文件
      if (imageName === 'touxiang1.jpg' || imageName === 'touxiang2.jpg') {
        // 对于本地测试文件，直接使用项目根目录的路径
        console.log('本地测试文件:', imageName)
        return `/${imageName}`
      }

      // 如果图片路径已经包含 /api/file/ 前缀，直接返回
      if (imageName.startsWith('/api/file/')) {
        console.log('已包含API前缀:', imageName)
        return imageName
      }

      // 如果图片路径以 /question-info/ 开头，添加 /api/file 前缀
      if (imageName.startsWith('/question-info/')) {
        const fullPath = `/api/file${imageName}`
        console.log('添加API前缀:', fullPath)
        return fullPath
      }

      // 如果图片路径以 / 开头但不是 /api/file/，可能是其他API路径
      if (imageName.startsWith('/')) {
        console.log('其他API路径:', imageName)
        return imageName
      }

      // 处理中文文件名，避免URL编码问题
      let processedImageName = imageName

      // 如果是中文文件名，尝试解码
      try {
        if (imageName.includes('%')) {
          processedImageName = decodeURIComponent(imageName)
          console.log('解码后的文件名:', processedImageName)
        }
      } catch (e) {
        console.warn('文件名解码失败:', e)
        processedImageName = imageName
      }

      // 尝试多种可能的路径格式
      const possiblePaths = [
        `/api/file/question-info/${processedImageName}`,
        `/api/file/${processedImageName}`,
        `/api/upload/${processedImageName}`,
        `/uploads/${processedImageName}`,
        `/static/${processedImageName}`,
        `/${processedImageName}`
      ]

      console.log('尝试路径:', possiblePaths[0])
      return possiblePaths[0]
    },

    // 获取图片名称（用于显示）
    getImageName(imageName) {
      if (!imageName) return ''
      // 提取文件名，去掉路径
      const parts = imageName.split('/')
      const fileName = parts[parts.length - 1]
      // 如果文件名太长，截取显示
      if (fileName.length > 10) {
        return fileName.substring(0, 10) + '...'
      }
      return fileName
    },

    // 预览图片
    previewImage(imageName) {
      if (!imageName) {
        this.$message.warning('图片不存在')
        return
      }
      const imageUrl = this.getImageUrl(imageName)
      console.log('预览图片:', imageUrl)

      // 使用Element UI的图片预览
      this.$alert(`<img src="${imageUrl}" style="max-width: 100%; max-height: 80vh; display: block; margin: 0 auto;" />`, '图片预览', {
        dangerouslyUseHTMLString: true,
        showClose: true,
        showCancelButton: false,
        confirmButtonText: '关闭',
        customClass: 'image-preview-dialog'
      }).catch(() => {})
    },

    // 处理图片加载错误
    handleImageError(event) {
      console.log('图片加载失败:', event.target.src)

      // 隐藏原始图片
      event.target.style.display = 'none'

      // 检查是否已经有错误占位符
      const existingPlaceholder = event.target.parentNode.querySelector('.image-error-placeholder')
      if (existingPlaceholder) {
        return
      }

      // 创建错误占位符
      const placeholder = document.createElement('div')
      placeholder.className = 'image-error-placeholder'
      placeholder.style.cssText = `
        width: 120px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f7fa;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        color: #c0c4cc;
        font-size: 14px;
        flex-direction: column;
      `
      placeholder.innerHTML = `
        <i class="el-icon-picture-outline" style="font-size: 20px; margin-bottom: 4px;"></i>
        <span style="font-size: 12px;">图片加载失败</span>
      `

      // 插入占位符
      event.target.parentNode.insertBefore(placeholder, event.target)
    },

    // 加载题库列表
    async loadQuestionBanks() {
      this.loading = true
      try {
        let response
        // 如果有courseId，则只加载当前课程的题库
        if (this.courseId) {
          console.log('加载当前课程的题库，课程ID:', this.courseId)
          response = await getQuestionBanksByCourseId(this.courseId)
        } else {
          // 否则加载当前教师的所有题库
          console.log('加载当前教师的所有题库')
          response = await getMyQuestionBanks()
        }

        console.log('题库列表API响应:', response)
        this.questionBanks = response.data || []
        console.log('题库列表数据:', this.questionBanks)

        // 检查每个题库是否包含courseId
        this.questionBanks.forEach((bank, index) => {
          console.log(`题库${index + 1}:`, {
            id: bank.id,
            name: bank.name,
            courseId: bank.courseId,
            hasCourseId: !!bank.courseId
          })
        })
      } catch (error) {
        console.error('加载题库列表失败:', error)
        this.$message.error('加载题库列表失败')
      } finally {
        this.loading = false
      }
    },

    // 加载课程列表
    async loadCourses() {
      try {
        console.log('开始加载课程列表...')
        const response = await getTeachingCourses()
        console.log('课程列表API响应:', response)
        this.courses = response.data || []
        console.log('课程列表数据:', this.courses)
        console.log('课程数量:', this.courses.length)

        // 检查每个课程的信息
        this.courses.forEach((course, index) => {
          console.log(`课程${index + 1}:`, {
            id: course.id,
            name: course.name,
            courseId: course.courseId
          })
        })
      } catch (error) {
        console.error('加载课程列表失败:', error)
        this.$message.error('加载课程列表失败')
      }
    },

    // 显示创建题库对话框
    showCreateBankDialog() {
      this.isEdit = false
      // 如果是从课程详情页面进入的，默认关联当前课程
      if (this.courseId) {
        this.bankForm.courseId = this.courseId
      }
      this.bankDialogVisible = true
    },

    // 编辑题库
    editBank(bank) {
      this.isEdit = true
      this.bankForm = { ...bank }
      this.bankDialogVisible = true
    },

    // 删除题库
    deleteBank(bank) {
      this.$confirm(`确定要删除题库"${bank.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteQuestionBank(bank.id)
          this.$message.success('删除成功')
          this.loadQuestionBanks()
        } catch (error) {
          this.$message.error('删除失败')
        }
      })
    },

    // 提交题库表单
    async submitBankForm() {
      this.$refs.bankForm.validate(async (valid) => {
        if (valid) {
          try {
            if (this.isEdit) {
              await updateQuestionBank(this.bankForm.id, this.bankForm)
              this.$message.success('更新成功')
            } else {
              await createQuestionBank(this.bankForm)
              this.$message.success('创建成功')
            }
            this.bankDialogVisible = false
            this.loadQuestionBanks()
          } catch (error) {
            this.$message.error(this.isEdit ? '更新失败' : '创建失败')
          }
        }
      })
    },

    // 重置题库表单
    resetBankForm() {
      this.bankForm = {
        name: '',
        description: '',
        courseId: null,
        isPublic: 0
      }
      this.$refs.bankForm && this.$refs.bankForm.resetFields()
    },

    // 打开题库详情
    async openBankDetail(bank) {
      console.log('打开题库详情，题库信息:', bank)
      this.currentBank = bank
      this.bankDetailVisible = true
      await this.loadFolderTree(bank.id)
      await this.loadBankQuestions(bank.id)
    },

    // 关闭题库详情
    closeBankDetail() {
      this.bankDetailVisible = false
      this.currentBank = null
      this.folderTree = []
      this.questions = []
    },

    // 加载文件夹树
    async loadFolderTree(bankId) {
      this.folderLoading = true
      try {
        const response = await getQuestionBankFolderTree(bankId)
        this.folderTree = response.data || []
        this.buildAllFoldersList(this.folderTree)
      } catch (error) {
        this.$message.error('加载文件夹失败')
      } finally {
        this.folderLoading = false
      }
    },

    // 构建所有文件夹列表（用于下拉选择）
    buildAllFoldersList(folders) {
      this.allFolders = []
      const traverse = (folderList) => {
        folderList.forEach(folder => {
          this.allFolders.push(folder)
          if (folder.children && folder.children.length > 0) {
            traverse(folder.children)
          }
        })
      }
      traverse(folders)
    },

    // 加载题库题目
    async loadBankQuestions(bankId) {
      this.questionLoading = true
      try {
        const response = await getBankQuestions(bankId)
        console.log('题库题目基础数据:', response.data)

        if (response.data && response.data.length > 0) {
          // 根据题目ID查询完整信息
          await this.enrichBankQuestionDetails(response.data)
        } else {
          this.questions = []
        }
      } catch (error) {
        console.error('加载题目失败:', error)
        this.$message.error('加载题目失败')
      } finally {
        this.questionLoading = false
      }
    },

    // 根据题目ID查询完整信息
    async enrichBankQuestionDetails(basicQuestions) {
      try {
        console.log('开始丰富题库题目详情，当前题库:', this.currentBank)
        console.log('基础题目数据:', basicQuestions)
        const enrichedQuestions = []

        for (const question of basicQuestions) {
          if (question.questionId || question.id) {
            const questionId = question.questionId || question.id
            try {
              console.log(`正在查询题库题目ID ${questionId} 的完整信息...`)
              const detailResponse = await quDetail(questionId)

              if (detailResponse && detailResponse.code === 1 && detailResponse.data) {
                const detailData = detailResponse.data
                console.log(`题库题目ID ${questionId} 的完整信息:`, detailData)

                // 合并基础数据和详情数据，优先使用详情数据
                const currentBankName = this.currentBank?.name || '未知题库'
                console.log(`题目ID ${questionId} 的所属题库设置:`, {
                  currentBankName: currentBankName,
                  detailDataRepoTitle: detailData.repoTitle,
                  questionRepoTitle: question.repoTitle,
                  finalRepoTitle: currentBankName
                })

                // 清理无效的图片路径
                let cleanImage = detailData.image || question.image || ''
                const invalidPaths = ['上传成功', 'success', '上传失败', 'failed', 'null', 'undefined']
                if (invalidPaths.includes(cleanImage) || !cleanImage.includes('.')) {
                  cleanImage = ''
                }

                const enrichedQuestion = {
                  ...question, // 保留基础数据
                  ...detailData, // 用详情数据覆盖
                  // 确保关键字段存在
                  id: detailData.id || questionId,
                  content: detailData.content || question.content || '',
                  image: cleanImage, // 使用清理后的图片路径
                  repoTitle: currentBankName,
                  quType: detailData.quType || question.quType || 1,
                  optionsStr: detailData.optionsStr || question.optionsStr || '',
                  answer: detailData.answer || question.answer || '',
                  analysis: detailData.analysis || question.analysis || '',
                  createTime: detailData.createTime || question.createTime || '',
                  knowledge: detailData.knowledge || question.knowledge || '',
                  point: detailData.point || question.point || 10,
                  realName: detailData.realName || question.realName || '',
                  type: detailData.type || question.type || '',
                  level: detailData.level || question.level || 1,
                  url: detailData.url || question.url || '',
                  difficulty: detailData.difficulty || question.difficulty || 0.5
                }

                enrichedQuestions.push(enrichedQuestion)
                console.log(`题库题目ID ${questionId} 信息已丰富`)
              } else {
                console.warn(`题库题目ID ${questionId} 查询详情失败，使用基础数据`)
                // 即使查询失败，也要确保显示正确的题库名称
                const fallbackQuestion = {
                  ...question,
                  repoTitle: this.currentBank?.name || question.repoTitle || '未知题库'
                }
                enrichedQuestions.push(fallbackQuestion)
              }
            } catch (error) {
              console.error(`查询题库题目ID ${questionId} 详情失败:`, error)
              enrichedQuestions.push(question) // 使用基础数据作为备选
            }
          } else {
            console.warn('题库题目缺少ID，跳过详情查询:', question)
            // 即使没有ID，也要确保显示正确的题库名称
            const noIdQuestion = {
              ...question,
              repoTitle: this.currentBank?.name || question.repoTitle || '未知题库'
            }
            enrichedQuestions.push(noIdQuestion)
          }
        }

        // 更新题目列表
        this.questions = enrichedQuestions
        console.log('题库题目信息丰富完成，最终数据:', this.questions)

        // 检查第一条记录的完整性
        if (enrichedQuestions.length > 0) {
          console.log('第一条题库题目完整数据示例:', enrichedQuestions[0])
          console.log('数据字段检查:')
          const firstQuestion = enrichedQuestions[0]
          console.log('- id:', firstQuestion.id)
          console.log('- content:', firstQuestion.content)
          console.log('- image:', firstQuestion.image)
          console.log('- repoTitle:', firstQuestion.repoTitle)
          console.log('- quType:', firstQuestion.quType)
          console.log('- optionsStr:', firstQuestion.optionsStr)
          console.log('- answer:', firstQuestion.answer)
          console.log('- analysis:', firstQuestion.analysis)
          console.log('- createTime:', firstQuestion.createTime)
          console.log('- knowledge:', firstQuestion.knowledge)
          console.log('- point:', firstQuestion.point)
          console.log('- realName:', firstQuestion.realName)
          console.log('- type:', firstQuestion.type)
          console.log('- level:', firstQuestion.level)
          console.log('- url:', firstQuestion.url)
          console.log('- difficulty:', firstQuestion.difficulty)
        }
      } catch (error) {
        console.error('丰富题库题目详情失败:', error)
        this.$message.warning('获取题目详情失败，显示基础信息')
        this.questions = basicQuestions
      }
    },

    // 加载文件夹题目
    async loadFolderQuestions(folderId) {
      if (!folderId) return
      this.questionLoading = true
      try {
        const response = await getFolderQuestions(folderId)
        console.log('文件夹题目基础数据:', response.data)

        if (response.data && response.data.length > 0) {
          // 根据题目ID查询完整信息
          await this.enrichBankQuestionDetails(response.data)
        } else {
          this.questions = []
        }
      } catch (error) {
        console.error('加载文件夹题目失败:', error)
        this.$message.error('加载文件夹题目失败')
      } finally {
        this.questionLoading = false
      }
    },

    // 点击文件夹节点
    onFolderNodeClick(data) {
      this.selectedFolderId = data && data.id
      // 便于后续导入/手动添加默认指向当前文件夹
      this.importFolderId = this.selectedFolderId
      this.loadFolderQuestions(this.selectedFolderId)
    },

    // 显示创建文件夹对话框
    showCreateFolderDialog() {
      this.isEditFolder = false
      this.folderForm.parentId = 0
      this.folderDialogVisible = true
    },

    // 显示创建子文件夹对话框
    showCreateSubFolderDialog(parentFolder) {
      this.isEditFolder = false
      this.folderForm.parentId = parentFolder.id
      this.folderDialogVisible = true
    },

    // 编辑文件夹
    editFolder(folder) {
      this.isEditFolder = true
      this.folderForm = { ...folder }
      this.folderDialogVisible = true
    },

    // 删除文件夹
    deleteFolder(folder) {
      this.$confirm(`确定要删除文件夹"${folder.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteQuestionBankFolder(folder.id)
          this.$message.success('删除成功')
          this.loadFolderTree(this.currentBank.id)
        } catch (error) {
          this.$message.error(error.message || '删除失败')
        }
      })
    },

    // 提交文件夹表单
    async submitFolderForm() {
      this.$refs.folderForm.validate(async (valid) => {
        if (valid) {
          try {
            if (this.isEditFolder) {
              await updateQuestionBankFolder(this.folderForm.id, this.folderForm)
              this.$message.success('更新成功')
            } else {
              const folderData = {
                ...this.folderForm,
                bankId: this.currentBank.id
              }
              await createQuestionBankFolder(this.currentBank.id, folderData)
              this.$message.success('创建成功')
            }
            this.folderDialogVisible = false
            this.loadFolderTree(this.currentBank.id)
          } catch (error) {
            this.$message.error(this.isEditFolder ? '更新失败' : '创建失败')
          }
        }
      })
    },

    // 重置文件夹表单
    resetFolderForm() {
      this.folderForm = {
        name: '',
        description: '',
        parentId: 0
      }
      this.$refs.folderForm && this.$refs.folderForm.resetFields()
    },

    // 移动题目
    moveQuestion(question) {
      // 实现移动题目逻辑
      this.$message.info('移动题目功能待实现')
    },

    // 编辑题目
    async editQuestion(question) {
      console.log('编辑题目，原始数据:', question)
      console.log('题目难度等级:', question.level, '类型:', typeof question.level)

      // 填充编辑表单数据
      this.editQuestionForm = {
        id: question.id,
        content: question.content || '',
        quType: question.quType || 1,
        optionsStr: question.optionsStr || '',
        answer: question.answer || '',
        analysis: question.analysis || '',
        knowledge: question.knowledge || '',
        knowledgePath: [], // 重置知识点路径
        point: question.point || 10,
        level: Number(question.level) || 1, // 确保转换为数字
        difficulty: question.difficulty || 0.5,
        image: question.image || '',
        link: question.url || question.link || '' // 添加链接字段
      }

      console.log('编辑表单数据:', this.editQuestionForm)
      console.log('表单难度等级:', this.editQuestionForm.level, '类型:', typeof this.editQuestionForm.level)

      // 验证难度等级值是否有效
      if (this.editQuestionForm.level < 1 || this.editQuestionForm.level > 4) {
        console.warn('难度等级值无效，重置为默认值1:', this.editQuestionForm.level)
        this.editQuestionForm.level = 1
      }

      // 清空图片文件列表
      this.editImageFileList = []

      // 加载课程知识点数据
      await this.loadCourseKnowledge()

      // 强制刷新知识点选项
      this.$nextTick(() => {
        console.log('知识点选项已刷新:', this.knowledgeOptions)
        // 强制触发响应式更新
        this.$forceUpdate()
        // 如果有知识点，尝试设置知识点路径
        if (question.knowledge) {
          this.setKnowledgePathFromName(question.knowledge)
        }
      })

      // 打开编辑弹窗
      this.editQuestionDialogVisible = true
    },

    // 保存OCR识别的题目到题库
    async saveOcrQuestionsToBank(ocrResults) {
      try {
        // 1. 先保存题目到t_question表
        const saveData = ocrResults.map(item => ({
          content: item.question_content || item.content || '',
          answer: item.answer || '',
          options: item.options && item.options.length > 0 ? item.options.join(';') : '',
          analysis: item.explanation || item.analysis || '',
          knowledge: item.knowledge || '',
          quType: this.convertQuType(item.question_type || item.type),
          version: '人教版',
          difficulty: 0.5,
          level: 2
        }))

        console.log('保存OCR题目数据到t_question表：', saveData)
        const saveResponse = await saveOcrQuestions(saveData)

        if (saveResponse.code === 1) {
          console.log('题目保存到t_question表成功')

          // 2. 获取保存后的题目ID并添加到题库关联表
          // 由于saveOcrQuestions可能不返回题目ID，我们需要通过内容查询获取ID
          for (const ocrQuestion of ocrResults) {
            try {
              // 通过题目内容查询获取题目ID
              const contentQuery = await quDetailbyContent(ocrQuestion.question_content || ocrQuestion.content)
              let questionId = null

              if (contentQuery && contentQuery.code === 1 && contentQuery.data) {
                // 处理不同的返回格式
                if (Array.isArray(contentQuery.data) && contentQuery.data.length > 0) {
                  questionId = contentQuery.data[0].id || contentQuery.data[0].questionId
                } else if (contentQuery.data.id) {
                  questionId = contentQuery.data.id
                }
              }

              if (questionId) {
                // 3. 将题目添加到题库关联表
                await addQuestionToFolder(this.currentBank.id, this.importFolderId || 0, questionId)
                console.log(`题目ID ${questionId} 已添加到题库关联表`)
              } else {
                console.warn(`无法获取题目ID，跳过添加到关联表: ${ocrQuestion.question_content || ocrQuestion.content}`)
              }
            } catch (error) {
              console.error('添加题目到题库关联表失败:', error)
            }
          }
        } else {
          console.error('保存题目到t_question表失败:', saveResponse.msg)
        }
      } catch (error) {
        console.error('保存OCR题目到题库失败:', error)
        this.$message.error('保存题目到题库失败，请稍后重试')
      }
    },

    // 转换题目类型
    convertQuType(type) {
      const typeMap = {
        'multiple_choice': 1,
        'choice': 1,
        'fill_blank': 2,
        'fill': 2,
        'true_false': 3,
        'judge': 3,
        'short_answer': 4,
        'application': 4
      }
      return typeMap[type] || 4
    },

    // 将选项字符串转换为选项数组
    parseOptionsFromString(optionsStr) {
      if (!optionsStr || optionsStr.trim() === '') {
        return []
      }

      // 按分号分割选项
      const optionTexts = optionsStr.split(';').map(opt => opt.trim()).filter(opt => opt !== '')

      // 转换为选项对象数组
      return optionTexts.map((text, index) => ({
        id: index + 1,
        content: text,
        isRight: false // 默认不是正确答案，需要根据答案字段判断
      }))
    },

    // 保存编辑的题目
    async saveEditQuestion() {
      try {
        // 表单验证
        await this.$refs.editQuestionForm.validate()

        this.editQuestionLoading = true

        // 准备保存数据 - 使用最简化的格式，只包含最基本的字段
        const saveData = {
          content: this.editQuestionForm.content || '',
          quType: Number(this.editQuestionForm.quType) || 1,
          answer: this.editQuestionForm.answer || '',
          analysis: this.editQuestionForm.analysis || '',
          knowledge: this.editQuestionForm.knowledge === '未分类' ? '' : (this.editQuestionForm.knowledge || ''),
          level: Number(this.editQuestionForm.level) || 1,
          difficulty: Number(this.editQuestionForm.difficulty) || 0.5,
          image: this.editQuestionForm.image || '',
          repoId: this.currentBank?.id || null,
          options: this.editQuestionForm.optionsStr || '',
          point: Number(this.editQuestionForm.point) || 10
        }

        // 尝试不同的字段名称组合
        console.log('尝试保存数据格式1:', saveData)

        // 如果第一种格式失败，尝试使用不同的字段名称
        const alternativeData = {
          content: this.editQuestionForm.content || '',
          quType: Number(this.editQuestionForm.quType) || 1,
          answer: this.editQuestionForm.answer || '',
          analysis: this.editQuestionForm.analysis || '',
          knowledge: this.editQuestionForm.knowledge === '未分类' ? '' : (this.editQuestionForm.knowledge || ''),
          level: Number(this.editQuestionForm.level) || 1,
          difficulty: Number(this.editQuestionForm.difficulty) || 0.5,
          image: this.editQuestionForm.image || '',
          repoId: this.currentBank?.id || null,
          optionsStr: this.editQuestionForm.optionsStr || '', // 尝试使用optionsStr
          point: Number(this.editQuestionForm.point) || 10
        }

        console.log('尝试保存数据格式2:', alternativeData)

        // 清理数据，移除可能的undefined或null值
        Object.keys(saveData).forEach(key => {
          if (saveData[key] === undefined || saveData[key] === null) {
            if (key === 'repoId') {
              saveData[key] = null
            } else if (typeof saveData[key] === 'number') {
              saveData[key] = 0
            } else {
              saveData[key] = ''
            }
          }
        })

        // 确保数字字段是数字类型
        saveData.quType = Number(saveData.quType)
        saveData.level = Number(saveData.level)
        saveData.difficulty = Number(saveData.difficulty)
        saveData.point = Number(saveData.point)
        saveData.repoId = saveData.repoId ? Number(saveData.repoId) : null

        console.log('保存题目数据:', saveData)
        console.log('保存题目数据JSON:', JSON.stringify(saveData, null, 2))
        console.log('题目ID:', this.editQuestionForm.id)
        console.log('当前题库ID:', this.currentBank?.id)
        console.log('编辑表单数据:', this.editQuestionForm)

        // 尝试最简化的数据格式
        const minimalData = {
          content: this.editQuestionForm.content || '',
          quType: Number(this.editQuestionForm.quType) || 1,
          answer: this.editQuestionForm.answer || ''
        }

        console.log('尝试最简化数据格式:', minimalData)

        // 尝试使用与成功示例相同的格式
        const postFormData = {
          id: this.editQuestionForm.id,
          content: this.editQuestionForm.content || '',
          quType: Number(this.editQuestionForm.quType) || 1,
          answer: this.editQuestionForm.answer || '',
          analysis: this.editQuestionForm.analysis || '',
          knowledge: this.editQuestionForm.knowledge === '未分类' ? '' : (this.editQuestionForm.knowledge || ''),
          level: Number(this.editQuestionForm.level) || 1,
          difficulty: Number(this.editQuestionForm.difficulty) || 0.5,
          image: this.editQuestionForm.image || '',
          repoId: this.currentBank?.id || null,
          options: this.editQuestionForm.optionsStr || '',
          point: Number(this.editQuestionForm.point) || 10
        }

        console.log('尝试使用postForm格式:', postFormData)

        // 使用完整的数据格式进行更新
        const completeData = {
          content: this.editQuestionForm.content || '',
          quType: Number(this.editQuestionForm.quType) || 1,
          answer: this.editQuestionForm.answer || '',
          analysis: this.editQuestionForm.analysis || '',
          knowledge: this.editQuestionForm.knowledge || '',
          level: Number(this.editQuestionForm.level) || 1,
          difficulty: Number(this.editQuestionForm.difficulty) || 0.5,
          image: this.editQuestionForm.image || null,
          repoId: Number(this.editQuestionForm.repoId) || this.currentBank?.id || 1,
          point: Number(this.editQuestionForm.point) || 10,
          options: this.editQuestionForm.options || null
        }
        console.log('使用完整数据格式:', completeData)

        // 使用完整数据格式进行更新
        let response = await quUpdate(this.editQuestionForm.id, completeData)

        // 如果失败，尝试postForm格式
        if (response.code === 0 && response.msg === '请求参数无法解析') {
          console.log('title格式失败，尝试postForm格式...')
          response = await quUpdate(this.editQuestionForm.id, postFormData)
        }

        // 如果失败，尝试最简化格式
        if (response.code === 0 && response.msg === '请求参数无法解析') {
          console.log('postForm格式失败，尝试最简化格式...')
          response = await quUpdate(this.editQuestionForm.id, minimalData)
        }

        // 如果最简化格式失败，尝试第一种格式
        if (response.code === 0 && response.msg === '请求参数无法解析') {
          console.log('最简化格式失败，尝试第一种格式...')
          response = await quUpdate(this.editQuestionForm.id, saveData)
        }

        // 如果第一种格式失败，尝试第二种格式
        if (response.code === 0 && response.msg === '请求参数无法解析') {
          console.log('第一种格式失败，尝试第二种格式...')
          response = await quUpdate(this.editQuestionForm.id, alternativeData)
        }

        // 如果所有格式都失败，显示详细的错误信息
        if (response.code === 0 && response.msg === '请求参数无法解析') {
          console.error('所有数据格式都失败了，可能是后端API问题或权限问题')
          console.error('题目ID:', this.editQuestionForm.id)
          console.error('当前用户权限可能不足，或者后端API端点有问题')

          // 尝试一个更直接的方法 - 检查是否有其他成功的更新示例
          console.log('尝试使用与成功示例完全相同的格式...')
          try {
            const successFormat = { title: this.editQuestionForm.content || '' }
            console.log('使用成功格式:', successFormat)
            const successResponse = await quUpdate(this.editQuestionForm.id, successFormat)
            console.log('成功格式响应:', successResponse)

            if (successResponse.code === 1) {
              this.$message.success('题目更新成功（使用成功格式）')
              this.editQuestionDialogVisible = false
              this.resetEditForm()
              this.loadBankQuestions(this.currentBank.id)
              return
            }
          } catch (error) {
            console.error('成功格式也失败了:', error)
          }

          // 尝试使用不同的API端点或方法
          console.log('尝试使用不同的API端点...')
          try {
            // 尝试使用POST方法而不是PUT方法
            const postData = {
              id: this.editQuestionForm.id,
              title: this.editQuestionForm.content || '',
              content: this.editQuestionForm.content || '',
              quType: Number(this.editQuestionForm.quType) || 1,
              answer: this.editQuestionForm.answer || '',
              analysis: this.editQuestionForm.analysis || '',
              knowledge: this.editQuestionForm.knowledge === '未分类' ? '' : (this.editQuestionForm.knowledge || ''),
              level: Number(this.editQuestionForm.level) || 1,
              difficulty: Number(this.editQuestionForm.difficulty) || 0.5,
              image: this.editQuestionForm.image || '',
              repoId: this.currentBank?.id || null,
              options: this.editQuestionForm.optionsStr || '',
              point: Number(this.editQuestionForm.point) || 10
            }
            console.log('尝试POST方法数据:', postData)

            // 使用POST方法调用API
            const postResponse = await axios.post(`/api/questions/${this.editQuestionForm.id}`, postData)
            console.log('POST方法响应:', postResponse)

            if (postResponse.data && postResponse.data.code === 1) {
              this.$message.success('题目更新成功（使用POST方法）')
              this.editQuestionDialogVisible = false
              this.resetEditForm()
              this.loadBankQuestions(this.currentBank.id)
              return
            }
          } catch (error) {
            console.error('POST方法也失败了:', error)
          }

          this.$message.error('保存失败：可能是权限问题或后端API问题，请联系管理员')
          return
        }
        console.log('更新API响应:', response)

        if (response.code === 1) {
          this.$message.success('题目更新成功')
          this.editQuestionDialogVisible = false
          // 清空表单和文件列表
          this.resetEditForm()
          // 刷新题目列表
          this.loadBankQuestions(this.currentBank.id)
        } else {
          console.error('更新失败响应:', response)
          this.$message.error(response.msg || '更新失败')
        }
      } catch (error) {
        console.error('保存题目失败:', error)
        console.error('错误详情:', {
          message: error.message,
          response: error.response,
          data: error.response?.data
        })
        if (error.message) {
          this.$message.error('保存失败：' + error.message)
        } else {
          this.$message.error('保存失败，请稍后重试')
        }
      } finally {
        this.editQuestionLoading = false
      }
    },

    // 处理编辑图片上传
    async handleEditImageChange(file, fileList) {
      // 检查文件大小（2MB限制）
      if (file.size > 2 * 1024 * 1024) {
        this.$message.error('图片大小不能超过2MB')
        return
      }

      // 检查文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.raw.type)) {
        this.$message.error('只能上传JPG、JPEG、PNG格式的图片')
        return
      }

      this.editImageFileList = fileList.slice(-1) // 只保留最新上传的文件

      try {
        // 上传图片到服务器
        const formData = new FormData()
        formData.append('file', file.raw)

        // 导入uploadFile函数
        const { uploadFile } = await import('@/api/homework')
        const response = await uploadFile(formData)

        if (response.code === 1) {
          // 上传成功，设置图片路径
          let imagePath = response.data || response.msg || file.name

          // 如果返回的是"上传成功"这样的消息，使用原始文件名
          if (imagePath === '上传成功' || imagePath === 'success' || !imagePath.includes('.')) {
            imagePath = file.name
          }

          // 确保图片路径是有效的文件名
          if (!imagePath || imagePath === 'null' || imagePath === 'undefined') {
            imagePath = file.name
          }

          this.editQuestionForm.image = imagePath
          this.$message.success('图片上传成功')
          console.log('编辑图片上传成功:', this.editQuestionForm.image)
        } else {
          this.$message.error('图片上传失败: ' + (response.msg || '未知错误'))
          // 上传失败时清空文件列表
          this.editImageFileList = []
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message.error('图片上传失败，请稍后重试')
        // 上传失败时清空文件列表
        this.editImageFileList = []
      }
    },

    // 处理编辑图片移除
    handleEditImageRemove(file, fileList) {
      this.editImageFileList = fileList
      this.editQuestionForm.image = ''
      console.log('编辑图片移除')
    },

    // 移除编辑图片
    removeEditImage() {
      this.editQuestionForm.image = ''
      this.editImageFileList = []
    },

    // 重置编辑表单
    resetEditForm() {
      this.editQuestionForm = {
        id: null,
        content: '',
        quType: 1,
        optionsStr: '',
        answer: '',
        analysis: '',
        knowledge: '',
        knowledgePath: [], // 重置知识点路径
        point: 10,
        level: 1,
        difficulty: 0.5,
        image: '',
        link: '' // 重置链接字段
      }
      this.editImageFileList = []
      this.knowledgeOptions = [] // 清空知识点选项
    },

    // 移除题目
    removeQuestion(question) {
      this.$confirm(`确定要从题库中移除题目"${question.title}"吗？`, '确认移除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await removeQuestionFromFolder(this.currentBank.id, question.questionId)
          this.$message.success('移除成功')
          this.loadBankQuestions(this.currentBank.id)
        } catch (error) {
          this.$message.error('移除失败')
        }
      })
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleDateString()
    },

    // 格式化URL，确保有协议前缀
    formatUrl(url) {
      if (!url) return ''

      // 如果URL已经包含协议，直接返回
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }

      // 如果没有协议，默认添加https://
      return 'https://' + url
    },

    // 获取URL显示文本
    getUrlDisplayText(url) {
      if (!url) return ''

      // 如果URL太长，截取显示
      if (url.length > 20) {
        return url.substring(0, 17) + '...'
      }

      return url
    },

    // 处理导入命令
    handleImportCommand(command) {
      if (!this.currentBank) {
        this.$message.warning('请先选择一个题库')
        return
      }

      switch (command) {
        case 'manual':
          // 强制用户选择目标文件夹
          this.importFolderId = null
          // 加载课程知识点数据
          this.loadCourseKnowledge()
          this.manualAddDialogVisible = true
          break
        case 'excel':
          this.importFolderId = null
          this.excelImportDialogVisible = true
          break
        case 'ocr':
          this.importFolderId = null
          this.ocrImportDialogVisible = true
          break
        case 'pdf':
          this.importFolderId = null
          this.pdfImportDialogVisible = true
          break
      }
    },

    // Excel文件处理
    handleExcelFileChange(file, fileList) {
      this.excelFileList = fileList.slice(-1)
    },

    handleExcelFileRemove(file, fileList) {
      this.excelFileList = fileList
    },

    async submitExcelFile() {
      if (!this.excelFileList || this.excelFileList.length === 0) {
        this.$message.warning('请先选择Excel文件')
        return
      }

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.excelFileList[0].raw)

        // TODO: 调用后端API导入Excel，需要传入题库ID和文件夹ID
        // await importQue(repoId, formData)

        this.$message.success('Excel导入成功')
        this.excelImportDialogVisible = false
        this.excelFileList = []
        this.loadFolderTree(this.currentBank.id)
        this.loadBankQuestions(this.currentBank.id)
      } catch (error) {
        this.$message.error('Excel导入失败：' + (error.message || '未知错误'))
      } finally {
        this.uploading = false
      }
    },

    // OCR文件处理
    handleOcrFileChange(file, fileList) {
      this.ocrFileList = fileList
    },

    handleOcrFileRemove(file, fileList) {
      this.ocrFileList = fileList
    },

    handleAnswersFileChange(file, fileList) {
      this.answersFileList = fileList
    },

    handleAnswersFileRemove(file, fileList) {
      this.answersFileList = fileList
    },

    async submitOcrFiles() {
      if (!this.ocrFileList || this.ocrFileList.length === 0) {
        this.$message.warning('请先选择题目图片文件')
        return
      }

      this.uploading = true
      try {
        const results = []
        for (let i = 0; i < this.ocrFileList.length; i++) {
          const file = this.ocrFileList[i]
          const formData = new FormData()
          formData.append('image', file.raw)

          // 添加对应的答案图片（如果有）
          if (this.answersFileList && this.answersFileList.length > 0) {
            // 如果答案图片数量与题目图片数量相同，则一一对应
            if (this.answersFileList.length === this.ocrFileList.length) {
              formData.append('answers', this.answersFileList[i].raw)
            } else if (this.answersFileList.length === 1) {
              // 如果只有一张答案图片，则所有题目都使用这张答案图片
              formData.append('answers', this.answersFileList[0].raw)
            } else {
              formData.append('answers', 'null')
            }
          } else {
            formData.append('answers', 'null')
          }

          const response = await processOcrImage(formData)
          // 修复：正确访问response.data.questions
          const questions = response.data && response.data.questions ? response.data.questions : response.questions

          if (questions && Array.isArray(questions)) {
            results.push(...questions)
          }
        }

        // 保存OCR识别的题目到题库
        if (results.length > 0) {
          await this.saveOcrQuestionsToBank(results)
        }

        this.$message.success(`OCR识别成功，共识别${results.length}道题目`)
        this.ocrImportDialogVisible = false
        this.ocrFileList = []
        this.answersFileList = []
        this.loadFolderTree(this.currentBank.id)
        this.loadBankQuestions(this.currentBank.id)
      } catch (error) {
        this.$message.error('OCR识别失败：' + (error.message || '未知错误'))
      } finally {
        this.uploading = false
      }
    },

    // PDF文件处理
    handlePdfFileChange(file, fileList) {
      this.pdfFileList = fileList.slice(-1)
    },

    handlePdfFileRemove(file, fileList) {
      this.pdfFileList = fileList
    },

    async submitPdfFile() {
      if (!this.pdfFileList || this.pdfFileList.length === 0) {
        this.$message.warning('请先选择PDF文件')
        return
      }

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('pdf_file', this.pdfFileList[0].raw)

        // 调用PDF解析API
        const response = await axios.post('http://123.56.176.195:8000/process_pdf/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        const payload = (response && response.data) || response || {}
        const results = payload.data || payload.results || payload.questions || []

        // TODO: 保存PDF解析的题目到题库，需要传入题库ID和文件夹ID
        // await savePdfQuestions(results, this.currentBank.id, this.importFolderId)

        this.$message.success(`PDF解析成功，共解析${results.length}道题目`)
        this.pdfImportDialogVisible = false
        this.pdfFileList = []
        this.loadFolderTree(this.currentBank.id)
        this.loadBankQuestions(this.currentBank.id)
      } catch (error) {
        this.$message.error('PDF解析失败：' + (error.message || '未知错误'))
      } finally {
        this.uploading = false
      }
    },

    // 手动添加题目
    async submitManualQuestion() {
      if (!this.manualQuestionForm.content) {
        this.$message.warning('请输入题目内容')
        return
      }

      // 校验必须选择有效的目标文件夹（非0）
      if (!this.importFolderId || this.importFolderId === 0) {
        this.$message.warning('请选择目标文件夹')
        return
      }

      // 验证选择题选项
      if (this.manualQuestionForm.quType === 1) {
        const options = this.parseOptions(this.manualQuestionForm.options)
        if (options.length < 2) {
          this.$message.warning('选择题至少需要两个选项')
          return
        }
      }

      this.uploading = true
      try {
        // 1. 先创建题目
        const questionData = {
          content: this.manualQuestionForm.content, // 题干
          quType: this.manualQuestionForm.quType, // 题目类型
          answer: this.manualQuestionForm.answer, // 答案
          analysis: this.manualQuestionForm.analysis, // 解析
          knowledge: this.manualQuestionForm.knowledge, // 知识点
          level: this.manualQuestionForm.level, // 难度等级
          point: this.manualQuestionForm.point, // 分数
          repoId: 1, // 默认题库ID
          difficulty: 0.5, // 默认难度系数
          options: this.manualQuestionForm.quType === 1 ? this.parseOptions(this.manualQuestionForm.options) : null // 选择题选项
        }

        const questionResponse = await quAdd(questionData)
        const questionId = typeof questionResponse.data === 'number'
          ? questionResponse.data
          : (questionResponse.data && questionResponse.data.id)
        if (!questionId) {
          this.$message.error('题目ID获取失败')
          this.uploading = false
          return
        }

        // 2. 将题目添加到题库文件夹
        await addQuestionToFolder(this.currentBank.id, this.importFolderId, questionId)

        this.$message.success('题目添加成功')
        this.manualAddDialogVisible = false
        this.manualQuestionForm = {
          content: '',
          quType: 1,
          options: '',
          answer: '',
          analysis: '',
          knowledge: '',
          knowledgePath: [] // 重置知识点路径
        }
        this.loadFolderTree(this.currentBank.id)
        if (this.selectedFolderId) {
          this.loadFolderQuestions(this.selectedFolderId)
        } else {
          this.loadBankQuestions(this.currentBank.id)
        }
      } catch (error) {
        this.$message.error('题目添加失败：' + (error.message || '未知错误'))
      } finally {
        this.uploading = false
      }
    },

    // 解析选择题选项
    parseOptions(optionsString) {
      if (!optionsString || !optionsString.trim()) return []

      const options = []
      const optionArray = optionsString.split(';')

      optionArray.forEach((option, index) => {
        const trimmedOption = option.trim()
        if (trimmedOption) {
          options.push({
            id: index + 1,
            content: trimmedOption,
            isCorrect: 0 // 默认不是正确答案，需要根据答案字段判断
          })
        }
      })

      return options
    }
  }
}
</script>

<style scoped>
.question-bank-management {
  padding: 20px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

  /* 题目列表样式 */
  .question-list {
    width: 100%;
    overflow-x: auto;
  }

  .question-list .el-table {
    min-width: 2000px;
  }

  /* URL链接样式 */
  .url-link {
    color: #409EFF;
    text-decoration: none;
    cursor: pointer;
    font-size: 12px;
    display: inline-block;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .url-link:hover {
    color: #66b1ff;
    text-decoration: underline;
  }

  .url-link:visited {
    color: #8c8c8c;
  }

  .page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.bank-list {
  min-height: 400px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

.bank-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.bank-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.bank-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.bank-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.bank-actions {
  opacity: 0;
  transition: opacity 0.3s;
}

.bank-card:hover .bank-actions {
  opacity: 1;
}

.bank-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 12px;
  line-height: 1.5;
}

.bank-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.meta-item {
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  margin-right: 4px;
}

.bank-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.bank-status {
  padding: 2px 8px;
  border-radius: 4px;
  background: #f4f4f5;
  color: #909399;
}

.bank-status.public {
  background: #f0f9ff;
  color: #409eff;
}

.bank-time {
  color: #c0c4cc;
}

.bank-detail {
  max-height: 70vh;
  overflow-y: auto;
}

.bank-info {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
}

.bank-info h3 {
  margin: 0 0 8px 0;
  color: #303133;
}

.bank-stats {
  display: flex;
  gap: 20px;
  margin-top: 12px;
  font-size: 14px;
  color: #606266;
}

.folder-management,
.question-management {
  margin-bottom: 20px;
}

.folder-header,
.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.folder-header h4,
.question-header h4 {
  margin: 0;
  color: #303133;
}

.folder-tree {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px;
  min-height: 200px;
}

.custom-tree-node {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.tree-node-label {
  display: flex;
  align-items: center;
}

.tree-node-label i {
  margin-right: 8px;
  color: #409eff;
}

.question-count {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}

.tree-node-actions {
  opacity: 0;
  transition: opacity 0.3s;
}

.custom-tree-node:hover .tree-node-actions {
  opacity: 1;
}

.add-question-content {
  padding: 20px 0;
}

.dialog-footer {
  text-align: right;
}

/* 确保编辑题目对话框可以正常操作 */
.edit-question-dialog {
  z-index: 3000 !important;
}

.edit-question-dialog .el-dialog {
  z-index: 3001 !important;
  pointer-events: auto !important;
}

.edit-question-dialog .el-dialog__wrapper {
  z-index: 3000 !important;
  pointer-events: auto !important;
}

.edit-question-dialog .el-dialog__body {
  pointer-events: auto !important;
}

.edit-question-dialog .el-form {
  pointer-events: auto !important;
}

.edit-question-dialog .el-form-item {
  pointer-events: auto !important;
}

.edit-question-dialog .el-select {
  pointer-events: auto !important;
}

.edit-question-dialog .el-select-dropdown {
  z-index: 3002 !important;
}

.edit-question-dialog .el-cascader {
  pointer-events: auto !important;
}

.edit-question-dialog .el-cascader-menus {
  z-index: 3002 !important;
}

.edit-question-dialog .el-cascader .el-input__inner {
  pointer-events: auto !important;
  cursor: pointer !important;
}

.edit-question-dialog .el-cascader .el-input__suffix {
  pointer-events: auto !important;
}

.edit-question-dialog .el-cascader .el-input__suffix .el-input__icon {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* 确保编辑题目对话框中的级联选择器下拉框可以正常显示 */
.edit-question-cascader-dropdown {
  z-index: 3004 !important;
  pointer-events: auto !important;
  position: relative !important;
}

.edit-question-cascader-dropdown .el-cascader-menu {
  pointer-events: auto !important;
  display: block !important;
  visibility: visible !important;
}

.edit-question-cascader-dropdown .el-cascader-menu__item {
  pointer-events: auto !important;
  cursor: pointer !important;
  display: block !important;
  visibility: visible !important;
}

.edit-question-cascader-dropdown .el-cascader-menu__item:hover {
  background-color: #f5f7fa !important;
}

/* 确保级联选择器面板可见 */
.el-cascader-panel {
  z-index: 3004 !important;
}

.el-cascader-panel .el-cascader-menu {
  z-index: 3004 !important;
}

.edit-question-dialog .el-input-number {
  pointer-events: auto !important;
}

/* 简化el-select样式 */
.edit-question-dialog .el-select {
  pointer-events: auto !important;
}

.edit-question-dialog .el-select .el-input {
  pointer-events: auto !important;
}

.edit-question-dialog .el-select .el-input__inner {
  pointer-events: auto !important;
  cursor: pointer !important;
}

.edit-question-dialog .el-select .el-input__suffix {
  pointer-events: auto !important;
}

.edit-question-dialog .el-select .el-input__suffix .el-input__icon {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* 图片单元格样式 */
.image-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.thumbnail-image {
  width: 60px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e4e7ed;
  object-fit: cover;
  transition: all 0.3s ease;
}

.thumbnail-image:hover {
  border-color: #409eff;
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.image-name {
  font-size: 12px;
  color: #666;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-error-placeholder {
  width: 60px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  color: #c0c4cc;
}

/* 图片预览对话框样式 */
.image-preview-dialog .el-message-box {
  width: auto;
  max-width: 90vw;
}

.image-preview-dialog .el-message-box__content {
  padding: 20px;
}

/* 表单提示样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
